import React from 'react';
import _ from 'lodash';

import {
  Control,
  Controller,
  FieldError,
  FieldPath,
  FieldValues,
  UseControllerProps
} from 'react-hook-form';
import {
  validationUtil,
  ContainerBox
} from '../../../layer0';
import {
  BaseTextFieldProps,
  SxProps,
  TextField as BaseTextField
} from '@mui/material';




export interface IProps {
  label?: string;
  maxLengthForLabelLengthCounter?: number;
  type?: 'text' | 'email' | 'password' | 'tel';
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  errorMessage?: string;
  isDisabled?: boolean;
  placeholder?: string;
  numLines?: number;
  size?: BaseTextFieldProps['size'];
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  disableDefaultMargins?: boolean;
  disableDefaultPadding?: boolean;
  sx?: SxProps;
}

export const FC: React.FC<IProps> = props => {
  const [internalValue, setInternalValue] = React.useState(props.value);

  const length = React.useMemo(
    () => props.value.length,
    [props.value]
  );

  const label = React.useMemo(
    () => !_.isNil(props.maxLengthForLabelLengthCounter) ?
      `${props.label ?? ""} (${length}/${props.maxLengthForLabelLengthCounter})`.trim() :
      props.label,
    [
      props.label,
      length,
      props.maxLengthForLabelLengthCounter
    ]
  );



  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    props.onChange(event.target.value);
    if (props.onSubmit) {
      setInternalValue(event.target.value);
    }
  }



  function handleKeyPress(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter') {
      props.onSubmit?.(internalValue);
    }
  }



  return (
    <ContainerBox.FC disableDefaultMargins={props.disableDefaultMargins}>
      <BaseTextField
        label={label}
        type={props.type}
        value={props.value ?? ""}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        error={!!props.errorMessage}
        helperText={props.errorMessage}
        variant="outlined"
        disabled={props.isDisabled}
        placeholder={props.placeholder}
        multiline={!_.isUndefined(props.numLines) && props.numLines > 1}
        rows={props.numLines}
        size={props.size}
        InputProps={{
          startAdornment: props.startAdornment,
          endAdornment: props.endAdornment
        }}
        fullWidth
        sx={{
          ...props.sx,
          '& .MuiInputBase-root': {
            padding: props.disableDefaultPadding ? 0 : undefined,
            overflow: props.disableDefaultPadding ? 'hidden' : undefined
          }
        }} />
    </ContainerBox.FC>
  );
};



const __fieldErrorType2ErrorMessage: Partial<Record<FieldError['type'], string>> = {
  max: "Number is too large.",
  maxLength: "Text is too long.",
  min: "Number is too small.",
  minLength: "Text is too short.",
  pattern: "Incorrect format.",
  validate: "Validation failed."
};



export type FnValidate = (value: string) => string | boolean;

export interface ITextFieldControllerProps<TFormState extends FieldValues> {
  formControl: Control<TFormState>;
  fieldKey: FieldPath<TFormState>;
  isRequired?: boolean;
  label?: string;
  type?: IProps['type'];
  isDisabled?: boolean;
  placeholder?: string;
  numLines?: number;
  size?: BaseTextFieldProps['size'];
  showLengthInLabel?: boolean;

  /**
   * Do not use the `validate` key. Use `extraValidationFunctions` instead.
   */
  extraValidationRules?: UseControllerProps['rules'];

  extraValidationFunctions?: FnValidate[];
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  disableDefaultMargins?: boolean;
  disableDefaultPadding?: boolean;
  sx?: SxProps;
}

export function genControllerComponent<TFormState extends FieldValues>() {
  const TextFieldController: React.FC<ITextFieldControllerProps<TFormState>> = props => {
    const maxLength = React.useMemo(
      () => {
        const value = props.extraValidationRules?.maxLength?.valueOf();
        return _.isNumber(value) ?
          value :
          undefined;
      },
      [props.extraValidationRules]
    );



    const {extraValidationFunctions} = props;
    const fnValidate = React.useMemo(
      () => extraValidationFunctions ?
        __genChainedValidateFunction(extraValidationFunctions) :
        (() => true),
      [extraValidationFunctions]
    );



    return (
      <Controller
        control={props.formControl}
        name={props.fieldKey}
        rules={{
          ...props.extraValidationRules,
          validate: props.isRequired ? {
            validate: fnValidate,
            __trimmedRequired: validationUtil.rules.trimmedRequired.validate
          } : fnValidate
        }}
        render={({field, fieldState}) => {
          const fieldStateError = fieldState.error;
          const errorMessage = (fieldStateError?.message) ||
            (__fieldErrorType2ErrorMessage[fieldStateError?.type ?? ''] ?? "");

          return (
            <FC
              label={props.label}
              maxLengthForLabelLengthCounter={props.showLengthInLabel ?
                maxLength :
                undefined}
              type={props.type}
              value={field.value}
              onChange={field.onChange}
              numLines={props.numLines}
              errorMessage={errorMessage}
              isDisabled={props.isDisabled}
              placeholder={props.placeholder}
              disableDefaultMargins={props.disableDefaultMargins}
              disableDefaultPadding={props.disableDefaultPadding}
              startAdornment={props.startAdornment}
              endAdornment={props.endAdornment}
              size={props.size}
              sx={props.sx} />
          );
        }} />
    );
  };

  return TextFieldController;
}



function __genChainedValidateFunction(validateFunctions: FnValidate[]) {
  return (value: string): string | boolean => {
    let retVal: string | boolean = true;

    _.forEach(validateFunctions, fnValidate => {
      retVal = fnValidate(value);

      if (
        (_.isBoolean(retVal) && !retVal) ||
        (_.isString(retVal) && !!retVal)
      ) {
        return false;
      }
    });

    return retVal;
  };
}
